import React from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ServerPackage {
  name: string;
  processor: string;
  ram: string;
  disk: string;
  bandwidth: string;
  originalPrice?: string;
  price: string;
  period: string;
  orderLink?: string;
  coupon?: string;
  discount?: string;
}

interface ServerPricingTableProps {
  packages: ServerPackage[];
  title: string;
  subtitle: string;
}

const ServerPricingTable = ({ packages, title, subtitle }: ServerPricingTableProps) => {
  return (
    <section className="py-20 bg-background">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="font-semibold text-foreground py-5 px-6">Plans</TableHead>
                  <TableHead className="font-semibold text-foreground py-5 px-6 text-center">Processor</TableHead>
                  <TableHead className="font-semibold text-foreground py-5 px-6 text-center">Ram</TableHead>
                  <TableHead className="font-semibold text-foreground py-5 px-6 text-center">Disk</TableHead>
                  <TableHead className="font-semibold text-foreground py-5 px-6 text-center">Bandwidth</TableHead>
                  <TableHead className="font-semibold text-foreground py-5 px-6 text-center">Price</TableHead>
                  <TableHead className="font-semibold text-foreground py-5 px-6 text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {packages.map((pkg, index) => {
                  const hasPromo = pkg.originalPrice || pkg.coupon;
                  return (
                    <React.Fragment key={pkg.name}>
                      <TableRow className={`hover:bg-muted/30 ${hasPromo ? 'border-b-0' : 'border-b border-border'}`}>
                        <TableCell className={`py-5 px-6 ${hasPromo ? 'pb-1' : ''}`}>
                          <span className="font-semibold text-primary">{pkg.name}</span>
                        </TableCell>
                        <TableCell className={`py-5 px-6 text-center text-muted-foreground ${hasPromo ? 'pb-1' : ''}`}>{pkg.processor}</TableCell>
                        <TableCell className={`py-5 px-6 text-center text-muted-foreground ${hasPromo ? 'pb-1' : ''}`}>{pkg.ram}</TableCell>
                        <TableCell className={`py-5 px-6 text-center text-muted-foreground ${hasPromo ? 'pb-1' : ''}`}>{pkg.disk}</TableCell>
                        <TableCell className={`py-5 px-6 text-center text-muted-foreground ${hasPromo ? 'pb-1' : ''}`}>{pkg.bandwidth}</TableCell>
                        <TableCell className={`py-5 px-6 text-center ${hasPromo ? 'pb-1' : ''}`}>
                          <span className="font-semibold text-foreground">৳{pkg.price}{pkg.period}</span>
                        </TableCell>
                        <TableCell className={`py-5 px-6 text-center ${hasPromo ? 'pb-1' : ''}`}>
                          {pkg.orderLink ? (
                            <Button size="sm" variant="gradient" asChild>
                              <a href={pkg.orderLink}>
                                Order Now
                              </a>
                            </Button>
                          ) : (
                            <Button size="sm" variant="gradient">
                              Order Now
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Show coupon/discount row if available - seamlessly connected to main row */}
                      {hasPromo && (
                        <TableRow className="border-b border-border hover:bg-muted/30">
                          <TableCell colSpan={7} className="pt-0 pb-4 px-6 text-center">
                            {pkg.originalPrice && (
                              <span className="text-sm">
                                Regular Price: <span className="text-destructive line-through">৳{pkg.originalPrice}{pkg.period}</span>
                                <span className="text-green-600 ml-2">Now {pkg.discount || '50%'} Off!</span>
                              </span>
                            )}
                            {pkg.coupon && (
                              <span className="text-red-600 font-semibold ml-3">Coupon: {pkg.coupon}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden grid gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.name}
              className="p-6 rounded-2xl bg-card border border-border"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-primary">{pkg.name}</h3>
                <div className="text-right">
                  {pkg.originalPrice && (
                    <div className="text-sm text-muted-foreground line-through">৳{pkg.originalPrice}{pkg.period}</div>
                  )}
                  <div className="font-bold text-foreground">৳{pkg.price}{pkg.period}</div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-muted-foreground mb-4">
                <div className="flex justify-between">
                  <span>Processor:</span>
                  <span className="text-foreground">{pkg.processor}</span>
                </div>
                <div className="flex justify-between">
                  <span>RAM:</span>
                  <span className="text-foreground">{pkg.ram}</span>
                </div>
                <div className="flex justify-between">
                  <span>Disk:</span>
                  <span className="text-foreground">{pkg.disk}</span>
                </div>
                <div className="flex justify-between">
                  <span>Bandwidth:</span>
                  <span className="text-foreground">{pkg.bandwidth}</span>
                </div>
              </div>

              {pkg.coupon && (
                <div className="text-center text-sm text-red-600 font-semibold mb-4">
                  Coupon: {pkg.coupon}
                </div>
              )}

              {pkg.orderLink ? (
                <Button variant="gradient" className="w-full" asChild>
                  <a href={pkg.orderLink}>
                    Order Now
                  </a>
                </Button>
              ) : (
                <Button variant="gradient" className="w-full">
                  Order Now
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServerPricingTable;
